/**
 * Schedule templates and copying a window of shifts (C1 to C5 in the old app).
 */
import { del, get, post } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiShift, invalidateShifts } from './shifts';

/* ── schedule templates (C1 to C5 in the old app) ───────────────────────── */
export interface ApiTemplate {
  id: number;
  name: string;
  description: string | null;
  template_type: string;
  shifts_count: number;
  users_count: number;
  date_range: string;
}
export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: ['templates'],
    enabled,
    queryFn: () => get<ApiTemplate[]>('/admin/schedule-templates'),
    staleTime: 60_000,
  });
}
/** One row of a plan the server has reviewed: what it would create, or why it would not. */
export interface ApiPlanRow {
  key: string;
  user_id: number | null;
  holder: string | null;
  open: boolean;
  shift_type_id: number | null;
  type_name: string | null;
  tasks: number;
  task_names: string[];
  start_date?: string;
  end_date?: string;
  start_clock?: string;
  end_clock?: string;
  overnight?: boolean;
  reason?: string;
  at?: string;
  existing_shift_id?: number;
}
export interface ApiPlan {
  zone: string;
  source?: { start_date: string; end_date: string; found?: number };
  target: { start_date: string; end_date: string };
  counts: {
    ready: number;
    blocked: number;
    excluded: number;
    assigned: number;
    open: number;
    tasks: number;
    existing_in_target: number;
  };
  ready: ApiPlanRow[];
  blocked: ApiPlanRow[];
  excluded: ApiPlanRow[];
  dry_run?: boolean;
  repeat?: boolean;
  created?: number;
  created_ids?: number[];
  template_name?: string;
  /** what the preview showed, so the apply can refuse work nobody reviewed */
  plan_token?: string;
}
export interface CopyWindowBody {
  source_start_date: string;
  source_end_date: string;
  target_start_date: string;
  target_end_date: string;
  create_open_shifts?: boolean;
  published_sources_only?: boolean;
  dry_run?: boolean;
  request_key?: string;
  plan_token?: string;
}
export interface ApplyTemplateBody {
  target_start_date: string;
  target_end_date: string;
  create_open_shifts?: boolean;
  published_sources_only?: boolean;
  dry_run?: boolean;
  request_key?: string;
  plan_token?: string;
}
export function useTemplateWrites() {
  const qc = useQueryClient();
  const done = () => Promise.all([qc.invalidateQueries({ queryKey: ['templates'] }), invalidateShifts(qc)]);
  return {
    /** The template stores the shifts it is given; the screen sends the window's shifts with their tasks. */
    create: useMutation({
      mutationFn: (b: {
        name: string;
        description?: string | null;
        template_type: string;
        start_date: string;
        end_date: string;
        shifts: Partial<ApiShift>[];
      }) => post<ApiTemplate>('/admin/schedule-templates', b),
      onSettled: done,
    }),
    /** Preview (`dry_run`) and apply share one route, so what is reviewed is what is created. */
    apply: useMutation({
      mutationFn: ({ id, body }: { id: number; body: ApplyTemplateBody }) =>
        post<ApiPlan>(`/admin/schedule-templates/${id}/apply`, body),
      onSettled: done,
    }),
    copyWindow: useMutation({
      mutationFn: (body: CopyWindowBody) => post<ApiPlan>('/admin/shifts/copy-window', body),
      onSettled: done,
    }),
    /** Read only: what happened to a reviewed request. Never creates, whatever it answers. */
    requestStatus: useMutation({
      mutationFn: (b: { action: 'copy_window' | 'template_apply'; request_key: string; payload: unknown }) =>
        post<{ state: 'completed' | 'pending' | 'absent' | 'other_owner'; result?: ApiPlan }>(
          '/admin/shifts/request-status',
          b,
        ),
    }),
    remove: useMutation({
      mutationFn: (id: number) => del<unknown>(`/admin/schedule-templates/${id}`),
      onSettled: done,
    }),
  };
}
